import React, { useState } from "react";
import { Field, Formik, Form } from "formik";
import * as Yup from "yup";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { Box, FormGroup, makeStyles } from "@material-ui/core";

import AppInput from "components/Input/AppInput";
import SelectField from "components/Select/SelectField";
import AppButton from "components/CustomButtons/AppButton";
import AppConfig, { EMPLOYMENT_TYPE, WORKER_TYPE } from "config/AppConfig";
import { YupValidations } from "utils/validate";
import { createWorker } from "store/slices/worker";
import { getAuthInfo } from "store/slices/auth";

const { PATTERNS } = AppConfig;

const styles = makeStyles((theme) => ({
  container: {
    padding: "2rem",
    // background: "#fff",
    // padding: "20px 30px",
    margin: "0 auto",
  },
  title: {
    fontSize: 22,
    fontWeight: 500,
    color: "#6CD0B3",
    marginBottom: "20px",
  },
  control: {
    display: 'flex',
    flexWrap: 'wrap',
    flexDirection: 'row',
    justifyContent: 'flex-end',
    marginTop: "20px",
    width: "50%",
    [theme.breakpoints.down('sm')]: {
      width: "100%",
    },
  },
  fieldWrapper: {
    width: "50%",
    [theme.breakpoints.down('sm')]: {
      width: "100%",
    },
  }
}));

const maskNumber = '999999999999999';

const StaffCreate = () => {
  const { t } = useTranslation();
  const classes = styles();
  const history = useHistory();
  const dispatch = useDispatch();
  
  const userInfo = useSelector(getAuthInfo);
  const { companyId } = userInfo || {};

  const [isSubmitting, setIsSubmitting] = useState(false);

  const roleOptions = Object.values(WORKER_TYPE).map(workerType => ({
    value: workerType,
    label: t(`worker_type.${workerType}`)
  }));

  const initForm = {
    email: '',
    firstName: '',
    lastName: '',
    phone: '',
    role: WORKER_TYPE.NURSE,
    employmentType: EMPLOYMENT_TYPE.CASUAL
  };

  const employmentTypeOptions = [
    { value: EMPLOYMENT_TYPE.CASUAL, label: t("label.employmentTypeCasual") },
    { value: EMPLOYMENT_TYPE.PART_TIME, label: t("label.employmentTypePartTime") },
    { value: EMPLOYMENT_TYPE.FULL_TIME, label: t("label.employmentTypeFullTime") }
  ];

  const validationSchema = Yup.object().shape({
    email: Yup.string().trim()
      .required('validation.email.required')
      .email('validation.email.invalid')
      .max(255, { message: 'validation.max_length', value: 255})
      .matches(PATTERNS.BLANK_SPACES, 'validation.blankSpaces'),
    firstName: YupValidations.requiredString.max(35, "validation.max_length_35"),
    lastName: YupValidations.requiredString.max(35, "validation.max_length_35"),
    phone: YupValidations.phone,
    role: Yup.string().required("validation.required"),
    employmentType: Yup.string().required("validation.required")
  });

  const handleSubmitForm = async (values) => {
    setIsSubmitting(true);
    try {
      const contentData = {
        companyId: companyId,
        workerType: values.role,
        firstName: values.firstName,
        lastName: values.lastName,
        email: values.email,
        phone: values.phone,
        employmentType: values.employmentType
      };

      const result = await dispatch(createWorker(contentData));
      if (createWorker.fulfilled.match(result)) {
        console.log(' >>> result.payload: ', result.payload);

        if (result.payload && result.payload.id) {
          history.push(`/admin/workers/${result.payload.id}`);
        }
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Box className={classes.container}>
        <Formik
          initialValues={initForm}
          validationSchema={validationSchema}
          onSubmit={handleSubmitForm}
        >
          {() => (
            <Form>
              <Box className={classes.fieldWrapper}>
                <Field
                  component={SelectField}
                  name="role"
                  label={"label.role"}
                  options={roleOptions}
                  required
                />
              </Box>
              <Box className={classes.fieldWrapper}>
                <Field
                  component={SelectField}
                  name="employmentType"
                  label={"label.employmentType"}
                  options={employmentTypeOptions}
                  required
                />
              </Box>
              <Box className={classes.fieldWrapper}>
                <Field
                  component={AppInput}
                  name="email"
                  id="email"
                  label={"label.email"}
                  required
                />
              </Box>
              <Box className={classes.fieldWrapper}>
                <Field
                  component={AppInput}
                  name="firstName"
                  id="firstName"
                  label={"label.firstName"}
                  required
                />
              </Box>
              <Box className={classes.fieldWrapper}>
                <Field
                  component={AppInput}
                  name="lastName"
                  id="lastName"
                  label={"label.lastName"}
                  required
                />
              </Box>
              <Box className={classes.fieldWrapper}>
                <Field
                  component={AppInput}
                  name="phone"
                  type="tel"
                  mask={maskNumber}
                  maskChar=""
                  id="phone"
                  label={"label.phone"}
                  required
                />
              </Box>
              <FormGroup className={classes.control}>
                <AppButton
                  label={t("button.saveAndContinue")}
                  rounder
                  type="submit"
                  isLoading={isSubmitting}
                  disabled={isSubmitting}
                  buttonColor="#6CD0B3"
                />
              </FormGroup>
            </Form>
          )}
        </Formik>
    </Box>
  );
};

export default StaffCreate;
